import * as React from "react";

import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { $api, fetchClient, invalidate } from "./client";
import type { components } from "./schema";

export type Item = components["schemas"]["Item"];
export type ItemLabel = components["schemas"]["ItemLabel"];

export interface ItemLabelListParams {
  search?: string;
  ordering?: string;
}

/** All item labels (tags): unpaginated. Pass `search`/`ordering` when driving
 *  a list view; call with no args for the cached full set. */
export const useItemLabels = (query: ItemLabelListParams = {}) =>
  $api.useQuery(
    "get",
    "/api/v1/item-labels/",
    { params: { query } },
    { staleTime: 5 * 60 * 1000 },
  );

export function useItemLabelName(): (id: number | null | undefined) => string {
  const { data } = useItemLabels();
  return React.useCallback(
    (id) => data?.find((l) => l.id === id)?.name ?? "",
    [data],
  );
}

export interface ItemListParams {
  search?: string;
  ordering?: string;
  labels?: number[];
  planningPolicy?: number[];
  withoutPolicy?: boolean;
  ids?: number[];
  limit?: number;
  offset?: number;
}

/** Paginated, searchable, filterable: the list page and the combobox wrappers
 *  all go through here. */
export function useItems({
  planningPolicy,
  withoutPolicy,
  ids,
  ...query
}: ItemListParams = {}) {
  return $api.useQuery(
    "get",
    "/api/v1/items/",
    {
      params: {
        query: {
          ...query,
          planning_policy: planningPolicy,
          without_policy: withoutPolicy,
          id__in: ids,
        },
      },
    },
    {
      // An explicitly-empty ids array means "resolve to nothing": don't fetch.
      enabled: ids === undefined || ids.length > 0,
      placeholderData: keepPreviousData,
    },
  );
}

export const useItem = (id: number | undefined) =>
  $api.useQuery(
    "get",
    "/api/v1/items/{id}/",
    { params: { path: { id: id ?? 0 } } },
    { enabled: id !== undefined },
  );

/** Atomically adds/removes labels across items. */
export function useBulkUpdateItemLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      itemIds: number[];
      add?: number[];
      remove?: number[];
    }) =>
      fetchClient.POST("/api/v1/items/bulk-labels/", {
        body: {
          itemIds: args.itemIds,
          add: args.add ?? [],
          remove: args.remove ?? [],
        },
      }),
    onSuccess: () => invalidate(queryClient, "/api/v1/items/"),
  });
}

export function useCreateItemLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; color: string }) =>
      (await fetchClient.POST("/api/v1/item-labels/", { body })).data!,
    onSuccess: () => invalidate(queryClient, "/api/v1/item-labels/"),
  });
}

export function useUpdateItemLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: number;
      name?: string;
      color?: string;
    }) =>
      (
        await fetchClient.PATCH("/api/v1/item-labels/{id}/", {
          params: { path: { id } },
          body,
        })
      ).data!,
    onSuccess: () => invalidate(queryClient, "/api/v1/item-labels/"),
  });
}

/** Deleting a label detaches it from every item that carries it. */
export function useDeleteItemLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchClient.DELETE("/api/v1/item-labels/{id}/", {
        params: { path: { id } },
      }),
    onSuccess: () =>
      invalidate(queryClient, "/api/v1/item-labels/", "/api/v1/items/"),
  });
}
